import { useState, useEffect } from 'react'
import { useApiWithSiteId } from '@src/hooks/useApiWithSiteId'
import { GetThreadMessages, GetThreadsBySite, GetUsersBySiteId, GetUserThreadByUserId, MarkThreadAsReadAdmin, SendMessageAdmin } from '@src/services/api-client'
import { useAuth } from '@src/modules/Auth/shared/state'
import { CreateThreadAdmin } from '@src/generatedClient/generated/taskreportsApi'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

export function useMessagesPanel(recordsPerPageDefault = 8) {
  const [textInput, setTextInput] = useState('')
  const { currentSite, currentSiteUser } = useAuth()
  const siteId = currentSite.id
  const [offset, setOffset] = useState(0)
  const [recordsPerPage, setRecordsPerPage] = useState(recordsPerPageDefault)

  const { call: fetchThreads, data, isCalling } = useApiWithSiteId(GetThreadsBySite, {
    siteId,
    offset,
    limit: recordsPerPage,
  })
  const { data: usersData, isCalling: isFetchingUsers } = useApiWithSiteId(GetUsersBySiteId, {
    limit: 10000,
    offset: 0,
  })
  const [selectedThreadId, setSelectedThreadId] = useState(null)
  const [threadMessages, setThreadMessages] = useState(null)
  const [isFetchingThreadMessages, setIsFetchingThreadMessages] = useState(false)
  const [isSelectingUser, setIsSelectingUser] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  const fetchThreadMessages = async (threadId) => {
    if (!threadId) {
      setThreadMessages({ messages: [] })
      return
    }
    setIsFetchingThreadMessages(true)
    try {
      const response = await GetThreadMessages({ threadId, siteId })
      setThreadMessages(response?.data)
    } catch (error) {
      console.error('Error fetching thread messages:', error)
    } finally {
      setIsFetchingThreadMessages(false)
    }
  }

  const handleSendMessage = async (values, { resetForm }) => {
    if (!values.text.trim()) return
    const newMessage = {
      siteId,
      threadId: selectedThreadId || (selectedUser ? undefined : null),
      text: values.text,
      ...(selectedUser && !selectedThreadId ? { userId: selectedUser.id } : {}),
    }
    try {
      const response = await SendMessageAdmin(newMessage as CreateThreadAdmin)
      const threadIdToFetch = selectedThreadId || response?.data?.id
      resetForm()
      setTextInput('')
      fetchThreads()
      if (!selectedThreadId) {
        setSelectedThreadId(response?.data?.id)
      }
      fetchThreadMessages(threadIdToFetch)
    } catch (error) {
      console.error('Error sending message:', error)
    }
  }

  useEffect(() => {
    if (selectedThreadId) {
      // Optionally log or handle thread selection
    }
  }, [selectedThreadId])

  const handleSelectMessage = async (thread) => {
    if (thread.unreadByAdmin) {
      try {
        await MarkThreadAsReadAdmin({
          siteId,
          threadId: thread.id,
        })
        fetchThreads()
      } catch (error) {
        console.error('Error marking thread as read:', error)
      }
    }
    setSelectedThreadId(thread.id)
    setSelectedUser(null)
    setIsSelectingUser(false)
    fetchThreadMessages(thread.id)
  }

  const handleStartNewMessage = () => {
    setIsSelectingUser(true)
    setSelectedThreadId(null)
    setThreadMessages(null)
    setSelectedUser(null)
  }

  const handleSelectUser = async (user) => {
    setSelectedUser(user)
    setIsSelectingUser(false)
    setSelectedThreadId(null)
    
    try {
      const response = await GetUserThreadByUserId({ siteId, userId: user.id })
      if (response.data) {
        setSelectedThreadId(response.data.id)
        fetchThreadMessages(response.data.id)
      } else {
        setThreadMessages({ messages: [] })
      }
    } catch (error) {
      console.error('Error checking user thread:', error)
      setThreadMessages({ messages: [] })
    }
  }

  const handleCancelSelectUser = () => {
    setIsSelectingUser(false)
    setSelectedUser(null)
    setThreadMessages(null)
    setSelectedThreadId(null)
  }

  const users = usersData?.users || []

  return {
    isSelectingUser,
    users,
    handleSelectUser,
    handleCancelSelectUser,
    data,
    selectedThreadId,
    handleSelectMessage,
    recordsPerPage,
    offset,
    setOffset,
    selectedUser,
    threadMessages,
    currentSiteUser,
    textInput,
    setTextInput,
    handleSendMessage,
    isCalling,
    isFetchingThreadMessages,
    isFetchingUsers,
    handleStartNewMessage,
  }
}
